import { PropertyDefinition, TypeDefinitionTypes } from '@eagle/common';
import { SharedThing } from '@eagle/core-data-types';
import { Card, CardContent, Checkbox, styled } from '@ethings/ui-toolkit';
import { Grid, useTheme } from '@mui/material';
import React, { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityTypes, useEntityDisplay } from '../../hooks/use-entity-display';
import { isValueObject } from '../../util';
import { getMaybePropertyValue } from '../format';
import { Icon } from '../icons';
import { ListPaperColumn } from '../list-item';

interface Props {
  sharedThing: SharedThing;
  onSelectedSharedThing: (sharedThing: SharedThing, selected: boolean) => void;
  showDetails: boolean;
  sharedThingTypeVehicleDefinitions?: Record<string, PropertyDefinition>;
  'data-testid'?: string;
}

interface DetailProps {
  xs: number;
  propertyKey: string;
  sharedThing: SharedThing;
  sharedThingTypeVehicleDefinitions?: Record<string, PropertyDefinition>;
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: 'min-content',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(1),
}));

const commonListProps = {
  sx: { justifyContent: 'stretch' },
  width: '100%',
  wordWrap: true,
  wrap: true,
  role: 'listitem',
};

const Detail: FC<DetailProps> = ({ xs, propertyKey, sharedThing, sharedThingTypeVehicleDefinitions }) => {
  const { t } = useTranslation(['common']);
  const label = sharedThingTypeVehicleDefinitions?.[propertyKey]?.label || t('common:component.shared-thing-card.hint.no-label');
  const rawValue = String(getMaybePropertyValue(sharedThing.properties[propertyKey]));
  const entityId = isValueObject(rawValue) ? String(rawValue.value) : String(rawValue);

  const isReference = sharedThingTypeVehicleDefinitions?.[propertyKey]?.type === TypeDefinitionTypes.REFERENCE;
  const isEntityType = ['brand', 'country', 'model', 'variant'].includes(propertyKey);

  const { display, loading } = useEntityDisplay(
    isEntityType ? (propertyKey as EntityTypes) : 'brand',
    isReference && isEntityType ? entityId : undefined,
  );

  const displayValue = isReference && isEntityType
    ? (loading ? t('common:common.labels.loading') : (display || entityId))
    : entityId || t('common:component.shared-thing-card.hint.no-value');

  return (
    <Grid item xs={xs} px={2}>
      <ListPaperColumn
        label={label}
        {...commonListProps}
      >
        {displayValue}
      </ListPaperColumn>
    </Grid>
  );
};

export const SharedThingCard: FC<Props> = memo(({ sharedThing, onSelectedSharedThing, showDetails, sharedThingTypeVehicleDefinitions, 'data-testid': dataTestId }) => {
  const theme = useTheme();
  const { t } = useTranslation(['common']);

  const handleToggle = (): void => {
    onSelectedSharedThing(sharedThing, !showDetails);
  };

  return (
    <StyledCard
      data-testid={dataTestId}
      role="checkbox"
      aria-label={t('common:page.thing-detail.title').toString()}
      aria-checked={showDetails}
      tabIndex={0}
      onClick={handleToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      }}
    >
      <StyledCardContent>
        <Grid
          container
          spacing={2}
          role="list"
          aria-label={t('common:common.labels.properties').toString()}
        >
          {Object.entries(sharedThing.properties).map(([propertyKey], index) => (
            <React.Fragment key={propertyKey}>
              {index === 0 ? (
                <>
                  <Detail
                    xs={11}
                    propertyKey={propertyKey}
                    sharedThing={sharedThing}
                    sharedThingTypeVehicleDefinitions={sharedThingTypeVehicleDefinitions}
                  />
                  <Grid item xs={1} pl={0}>
                    <Checkbox
                      checked={showDetails}
                      tabIndex={-1}
                      icon={<Icon icon="CheckCircleIcon" style={{ color: theme.palette.grey[300] }} />}
                      checkedIcon={<Icon icon="CheckCircleIcon" style={{ color: theme.palette.success.light }} />}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        onSelectedSharedThing(sharedThing, e.target.checked)
                      }
                    />
                  </Grid>
                </>
              ) : (
                showDetails && (
                  <Detail
                    xs={12}
                    propertyKey={propertyKey}
                    sharedThing={sharedThing}
                    sharedThingTypeVehicleDefinitions={sharedThingTypeVehicleDefinitions}
                  />
                )
              )}
            </React.Fragment>
          ))}
        </Grid>
      </StyledCardContent>
    </StyledCard>
  );
});

SharedThingCard.displayName = 'SharedThingCard';
